import React from 'react';
import PageHeader from '../components/PageHeader';
import { aboutStats, skills } from '../data/mock';
import { CheckCircle2, ArrowRight, Target, Eye, Heart, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollReveal, useCounter } from '../hooks/useScrollAnimation';

/* Animated stat card */
const StatCard = ({ value, label, start, delay }) => {
  const num = parseInt(value.replace(/\D/g, ''), 10);
  const suffix = value.replace(/[0-9]/g, '');
  const count = useCounter(num, 1600, start);
  return (
    <div
      className={`text-center text-white ${start ? 'anim-scale-in' : 'reveal-hidden'}`}
      style={{ animationDelay: delay }}
    >
      <div className="text-4xl font-extrabold tracking-tight mb-1">
        {start ? count : 0}{suffix}
      </div>
      <div className="text-sm font-medium text-green-100">{label}</div>
    </div>
  );
};

/* Animated skill bar */
const SkillBar = ({ label, value, visible, delay }) => (
  <div className={`${visible ? 'anim-fade-up' : 'reveal-hidden'}`} style={{ animationDelay: delay }}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-semibold text-foreground">{label}</span>
      <span className="text-sm font-bold" style={{ color: '#43a047' }}>{value}%</span>
    </div>
    <div className="bg-border rounded-full h-2.5 overflow-hidden">
      <div
        className={`h-2.5 rounded-full ${visible ? 'skill-bar-fill' : ''}`}
        style={{
          width: visible ? `${value}%` : '0%',
          backgroundColor: '#43a047',
          animationDuration: '1.3s',
          animationDelay: delay,
          animationTimingFunction: 'cubic-bezier(.22,.68,0,1.2)',
        }}
      />
    </div>
  </div>
);

const whoCards = [
  { icon: Target, title: 'Our Company Mission', desc: 'Our Lead Generation offer provides a cost-effective method of maximising your budget for International lead generation and corporate recruitment.' },
  { icon: Eye,    title: 'The B2BTechLeads Philosophy', desc: 'Working with you, we design an online lead generation strategy with clear goals and a streamlined lead qualification process with our sales lead tracking software.' },
  { icon: Heart,  title: 'The B2BTechLeads Promise', desc: "Our carefully measured lead generation programs keep our clients' sales funnels full and give them the competitive edge they need to win." },
  { icon: Zap,    title: 'We Can Deliver On Projects', desc: "Minimizing cost per lead while maximizing lead quality — that's the objective of our ongoing Lead Generation Capture and Lead Quality Optimization services." },
];

const AboutUs = () => {
  const [heroRef, heroVisible] = useScrollReveal({ threshold: 0.1 });
  const [statsRef, statsVisible] = useScrollReveal({ threshold: 0.3 });
  const [whoRef, whoVisible] = useScrollReveal({ threshold: 0.1 });
  const [skillRef, skillVisible] = useScrollReveal({ threshold: 0.2 });

  return (
    <div className="page-enter">
      <PageHeader title="About Us" breadcrumbs={[{ label: 'About Us' }]} />

      {/* ── Welcome ── */}
      <div className="py-16 bg-background" ref={heroRef}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-14 items-center">

            <div className={`flex-1 ${heroVisible ? 'anim-slide-right' : 'reveal-hidden'}`}>
              <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#43a047' }}>About Us</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground mb-3 leading-tight">Welcome To B2BTechLeads</h2>
              <div className="w-14 h-1 rounded-full mb-6" style={{ background: '#43a047' }} />
              <p className="text-muted-foreground leading-relaxed mb-4">
                B2BTechLeads was founded in 2008 with only 10 employees. Today, we are one of the global leaders in marketing outsourcing and database marketing, with over 200+ employees.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Our on-demand digital marketing platform enables businesses to turn their prospects into clients. B2BTechLeads gives a new meaning to the traditional marketing funnel, blending it with striking marketing automation capabilities.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                Our dynamic executions help B2B marketers generate and manage leads and drive sales across global markets.
              </p>
              <Link
                to="/contact"
                className="btn-pulse inline-flex items-center gap-2 text-white font-semibold px-7 py-3 rounded-full shadow-md text-sm"
                style={{ backgroundColor: '#43a047', transition: 'opacity 0.2s, transform 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Get In Touch <ArrowRight size={15} />
              </Link>
            </div>

            <div className={`flex-1 ${heroVisible ? 'anim-slide-left' : 'reveal-hidden'}`}>
              <div className="relative">
                <div className="absolute -bottom-3 -right-3 w-full h-full rounded-2xl" style={{ background: '#43a047', opacity: 0.12 }} />
                <img
                  src="https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?w=640&q=75"
                  alt="B2BTechLeads Team"
                  className="relative rounded-2xl shadow-xl w-full object-cover"
                  style={{ maxHeight: '380px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats Banner ── */}
      <div
        ref={statsRef}
        className="py-12 relative overflow-hidden"
        style={{ backgroundColor: '#43a047' }}
      >
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />
        <div className="relative max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {aboutStats.map((s, i) => (
              <StatCard key={i} value={s.value} label={s.label} start={statsVisible} delay={`${i * 0.1}s`} />
            ))}
          </div>
        </div>
      </div>

      {/* ── Who Is ── */}
      <div className="py-16 bg-muted" ref={whoRef}>
        <div className="max-w-6xl mx-auto px-4">
          <div className={`text-center mb-12 ${whoVisible ? 'anim-fade-up' : 'reveal-hidden'}`}>
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#43a047' }}>Our Identity</p>
            <h2 className="text-3xl font-extrabold text-foreground">Who Is B2BTechLeads</h2>
            <div className="w-14 h-1 mx-auto mt-3 rounded-full" style={{ background: '#43a047' }} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {whoCards.map((item, i) => (
              <div
                key={i}
                className={`card-hover bg-card p-6 border border-border rounded-xl group ${whoVisible ? 'anim-fade-up' : 'reveal-hidden'}`}
                style={{ animationDelay: `${i * 0.12}s` }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: '#e8f5e9', transition: 'background 0.25s' }}
                    onMouseEnter={e => e.parentElement.parentElement.style.borderColor = '#43a047'}
                  >
                    <item.icon size={20} style={{ color: '#43a047' }} />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground mb-2">{item.title}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Skills ── */}
      <div className="py-16 bg-background" ref={skillRef}>
        <div className="max-w-6xl mx-auto px-4">
          <div className={`${skillVisible ? 'anim-fade-up' : 'reveal-hidden'}`}>
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#43a047' }}>Expertise</p>
            <h2 className="text-3xl font-extrabold text-foreground mb-2">Our Crazy Skills</h2>
            <div className="w-14 h-1 rounded-full mb-10" style={{ background: '#43a047' }} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-7 max-w-3xl">
            {skills.map((skill, i) => (
              <SkillBar
                key={i}
                label={skill.label}
                value={skill.value}
                visible={skillVisible}
                delay={`${i * 0.15}s`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
